/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
  gridPositiveValueGetter,
  rendererIconByName,
  ROW_GROUP_CLASS,
  ROW_WARNING_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { DateTimeTypes, NumberTypes } from '@gh/common-shared';
import { SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
  safeGridOptionCallback,
} from '@gh/core-ui-grid';
import { WipBusinessService } from '../../resources/wip-business.service';
import { getWipJournalLogItemId, WipJournalLogItem } from '../../resources/wip.model';

@Component({
  standalone: false,
  selector: 'gh-wip-journal-log',
  templateUrl: 'wip-journal-log.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WipJournalLogComponent implements OnInit {
  @Input()
  dataSource: SlicedDataSource<WipJournalLogItem>;

  gridDefinition: GridDefinition;

  constructor(private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private business: WipBusinessService) {
  }

  ngOnInit(): void {
    this.gridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'entryDate',
          headerName: this.translations.get('lbl_entered_date'),
          width: 150,
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
        },
        {
          field: 'accountingDate',
          headerName: this.translations.get('lbl_accounting_date'),
          width: 150,
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
        },
        {
          field: 'accountCode',
          headerName: this.translations.get('lbl_account_code'),
          width: 150,
        },
        {
          field: 'accountName',
          headerName: this.translations.get('lbl_payment_account_name'),
          width: 400,
        },
        {
          field: 'debitAmount',
          headerName: this.translations.get('lbl_debit_dollar'),
          width: 150,
          ...buildNumberColumn(NumberTypes.Price),
          valueGetter: gridPositiveValueGetter,
        },
        {
          field: 'creditAmount',
          headerName: this.translations.get('lbl_credit_dollar'),
          width: 150,
          ...buildNumberColumn(NumberTypes.Price),
          valueGetter: gridPositiveValueGetter,
        },
        {
          field: 'reversed',
          headerName: this.translations.get('lbl_reversed'),
          width: 100,
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
      ],
      {
        getRowId: (params) => String(getWipJournalLogItemId(params.data)),
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_WARNING_CLASS]: safeGridOptionCallback(({ data }: { data: WipJournalLogItem }) => data.reversed),
          [ROW_GROUP_CLASS]: safeGridOptionCallback(({ data }: { data: WipJournalLogItem }) => data.groupSeq === 1),
        },
      });
  }
}
